import { Button, Flex, Paper, Stack, Title, Text, Space } from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { useUserDataStore } from '../../../shared_stores';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { useNavigate } from 'react-router-dom';
import {
  getReportCategoryFromParameter,
  useReportsStore,
} from '../../reports/stores';
import { Parameters } from '@web/common';
import { observer } from 'mobx-react-lite';
import { calculateCategory } from '../../reports';

export const GoalTrackerWidget = observer(() => {
  const { t } = useTranslation(['common', 'reports']);
  const navigate = useNavigate();
  const userDataStore = useUserDataStore();
  const reportsStore = useReportsStore();

  if (userDataStore.userGoal === undefined) {
    return (
      <Paper
        onClick={() => void navigate('/dashboard/goal')}
        style={{ cursor: 'pointer' }}
      >
        <Stack gap={0}>
          <EmojiEventsOutlinedIcon sx={{ fontSize: 34 }} />
          <Space h={'var(--mantine-spacing-md)'} />
          <Title order={2}>{t('reports:set_goal_title')}</Title>
          <Text>{t('reports:set_goal_message')}</Text>
        </Stack>
      </Paper>
    );
  }

  const userGoalParameter = userDataStore.userGoal.parameter;
  const data = reportsStore.getCategoryParameterValues(userGoalParameter);

  function getLastMeasurementValue(value: number) {
    switch (userGoalParameter) {
      case Parameters.BODY_WEIGHT:
      case Parameters.FAT_PERCENTAGE:
      case Parameters.FAT_MASS:
      case Parameters.MUSCLE_PERCENTAGE:
      case Parameters.MUSCLE_MASS:
      case Parameters.BODY_MASS_INDEX:
      case Parameters.BLOOD_PRESSURE:
      case Parameters.BLOOD_PRESSURE_SYSTOLIC:
      case Parameters.BLOOD_PRESSURE_DIASTOLIC:
      case Parameters.PULSE: {
        return value;
      }
      case Parameters.PHYSICAL_FITNESS:
      case Parameters.CARDIO_FITNESS:
      case Parameters.HEART_STRENGTH:
      case Parameters.CARDIOVASCULAR_RISK:
      case Parameters.STRESS_LEVEL: {
        const category = calculateCategory(
          value,
          reportsStore.getParameterThresholds(userGoalParameter),
        );
        return t(`reports:${category}`);
      }
    }
  }

  if (reportsStore.measurements?.length === 0) {
    return <Text>{t('reports:no_reports_message')}</Text>;
  }

  return (
    <Paper>
      <Stack gap={'var(--mantine-spacing-xs)'}>
        <Flex>
          <Stack gap={0} style={{ flexGrow: 1 }}>
            <Title order={3}>{t(`reports:${userGoalParameter}`)}</Title>
            <Text>
              {t('reports:last_measurement')}
              {getLastMeasurementValue(data[data.length - 1].series)}
              {reportsStore.getParameterUnit(userGoalParameter)}
            </Text>
          </Stack>
          <Flex direction={'column'}>
            <Button
              variant="icon"
              style={{ color: '#000', margin: 4 }}
              rightSection={<ChevronRightIcon />}
              onClick={() =>
                void navigate(
                  `/reports/${getReportCategoryFromParameter(userGoalParameter)}?parameter=${userGoalParameter}`,
                )
              }
            />
          </Flex>
        </Flex>
        <LineChart
          h={60}
          data={data}
          dataKey="date"
          series={[
            { name: 'series', color: 'var(--mantine-primary-color-filled)' },
          ]}
          curveType="linear"
          tickLine="none"
          gridAxis="none"
          withXAxis={false}
          withYAxis={false}
          yAxisProps={{
            domain: ([dataMin, dataMax]) => {
              const factor = 0.01;
              const min = dataMin - dataMin * factor;
              const max = dataMax + dataMax * factor;
              return [Math.floor(min), Math.ceil(max)];
            },
          }}
          withDots={false}
          withTooltip={false}
        />
        <Text
          style={{
            borderTop: '1px solid var(--mantine-color-otherGrey60-0)',
            textAlign: 'right',
          }}
        >
          {t('reports:goal')}:&nbsp;
          {t(`reports:${userDataStore.userGoal.targetValue}`)}
          {reportsStore.getParameterUnit(userGoalParameter)}
        </Text>
      </Stack>
    </Paper>
  );
});
